import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { UtilsService } from "./utils.service";
import { Observable } from "rxjs";
import { HttpHeaders } from "@angular/common/http";
import gql from "graphql-tag";
import { Apollo } from "apollo-angular";

@Injectable()
export class ApiService {
  constructor(
    private httpClient: HttpClient,
    private utilsService: UtilsService,
    private apollo: Apollo
  ) {}

  getUserRoles(): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.get(this.utilsService.apiEndpoint + "user-roles", {
      responseType: "json",
      headers: httpHeaders,
    });
  }

  getUserDetails(id): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.get(
      this.utilsService.apiEndpoint + "user-details/" + id,
      {
        responseType: "json",
        headers: httpHeaders,
      }
    );
  }

  getUserWishList(query): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.get(
      this.utilsService.apiEndpoint + "user-wishlist/" + query,
      {
        responseType: "json",
        headers: httpHeaders,
      }
    );
  }

  adminLogin(credentials): Observable<any> {
    return this.apollo.mutate({
      mutation: gql`
        mutation adminLogin($email: String!, $password: String!) {
          adminLogin(input: { email: $email, password: $password }) {
            token
          }
        }
      `,
      variables: {
        email: credentials.email,
        password: credentials.password,
      },
    });
  }

  getAllPhotos(obj: any) {
    // Initialize the variables object
    let variables = {
      first: obj.paginationData.pageSize,
      after:
        obj.paginationData.after !== undefined
          ? obj.paginationData.after
          : null,
      credit: null,
      attractionId: null,
      locationId: null,
      premium: null,
      private: null,
      width: obj.width || 150,
      height: obj.height || 150,
    };
    // Conditionally assign filter values
    if (obj.credit) {
      variables.credit = obj.credit;
    }
    if (obj.attractionId) {
      variables.attractionId = obj.attractionId;
    }
    if (obj.locationId) {
      variables.locationId = obj.locationId;
    }
    if (obj.premium !== undefined) {
      variables.premium = obj.premium; // Use `!==` to include false as well
    }
    if (obj.private !== undefined) {
      variables.private = obj.private; // Use `!==` to include false as well
    }

    // Construct the GraphQL query with dynamic variables
    return this.apollo.watchQuery({
      query: gql`
        query getPhotos(
          $first: Int
          $after: String
          $credit: String
          $attractionId: String
          $locationId: String
          $premium: Boolean
          $private: Boolean
          $width: Int
          $height: Int
        ) {
          photos(
            first: $first
            after: $after
            credit: $credit
            attractionId: $attractionId
            locationId: $locationId
            premium: $premium
            private: $private
          ) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                url(w: $width, h: $height)
                path
                credit
                editor
                description
                private
                premium
                mimetype
                originalUrl
                caption
                licensedBy
                owned
                licensed
                sublicenseable
              }
            }
            totalCount
          }
        }
      `,
      variables, // Pass the constructed variables
      errorPolicy: "all",
    }).valueChanges;
  }
  getAllVideos(obj?) {
    let variables = {};
    if (obj) {
      variables = {
        first: obj.paginationData.pageSize,
        after:
          obj.paginationData.after === undefined
            ? null
            : obj.paginationData.after.endCursor,
        credit: obj.credit || null,
        attractionId: obj.attractionId || null,
        locationId: obj.locationId || null,
      };
    }
    return this.apollo.watchQuery({
      query: gql`
        query getVideos(
          $first: Int
          $after: String
          $credit: String
          $attractionId: String
          $locationId: String
        ) {
          videos(
            first: $first
            after: $after
            credit: $credit
            attractionId: $attractionId
            locationId: $locationId
          ) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                url
                entity {
                  types
                  names
                }
              }
            }
            totalCount
          }
        }
      `,
      variables,
    }).valueChanges;
  }

  updateUser(data): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.put(
      this.utilsService.apiEndpoint + "update-user",
      data,
      {
        responseType: "json",
        headers: httpHeaders,
      }
    );
  }

  createTag(data): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.post(
      this.utilsService.apiEndpoint + "new-tag",
      data,
      {
        responseType: "json",
        headers: httpHeaders,
      }
    );
  }

  updateTag(data): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.put(
      this.utilsService.apiEndpoint + "update-tag",
      data,
      {
        responseType: "json",
        headers: httpHeaders,
      }
    );
  }

  removeTag(id): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.delete(
      this.utilsService.apiEndpoint + "delete-tag/" + id,
      {
        responseType: "json",
        headers: httpHeaders,
      }
    );
  }

  getLocation(id): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.get(
      this.utilsService.apiEndpoint + "location-view/" + id,
      {
        responseType: "json",
        headers: httpHeaders,
      }
    );
  }

  createLocation(data): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.post(
      this.utilsService.apiEndpoint + "new-tag",
      data,
      {
        responseType: "json",
        headers: httpHeaders,
      }
    );
  }

  createAttraction(data): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.post(
      this.utilsService.apiEndpoint + "new-attraction",
      data,
      {
        responseType: "json",
        headers: httpHeaders,
      }
    );
  }

  getAttraction(id): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.get(
      this.utilsService.apiEndpoint + "attraction-view/" + id,
      {
        responseType: "json",
        headers: httpHeaders,
      }
    );
  }

  getAttractionTypes(): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.get(
      this.utilsService.apiEndpoint + "attraction-types",
      {
        responseType: "json",
        headers: httpHeaders,
      }
    );
  }

  createAgency(data): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.post(
      this.utilsService.apiEndpoint + "new-agency",
      data,
      {
        responseType: "json",
        headers: httpHeaders,
      }
    );
  }

  getAgency(id): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.get(
      this.utilsService.apiEndpoint + "agency-details/" + id,
      {
        responseType: "json",
        headers: httpHeaders,
      }
    );
  }

  addGuide(data): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.post(
      this.utilsService.apiEndpoint + "new-guide",
      data,
      {
        responseType: "json",
        headers: httpHeaders,
      }
    );
  }

  getGuide(id): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.get(
      this.utilsService.apiEndpoint + "guide-view/" + id,
      {
        responseType: "json",
        headers: httpHeaders,
      }
    );
  }

  addLanguage(data): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.post(
      this.utilsService.apiEndpoint + "new-language",
      data,
      {
        responseType: "json",
        headers: httpHeaders,
      }
    );
  }

  updateLanguage(data): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.put(
      this.utilsService.apiEndpoint + "update-language",
      data,
      {
        responseType: "json",
        headers: httpHeaders,
      }
    );
  }

  removeLanguage(id): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.delete(
      this.utilsService.apiEndpoint + "delete-language/" + id,
      {
        responseType: "json",
        headers: httpHeaders,
      }
    );
  }

  addDoc(data): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.post(
      this.utilsService.apiEndpoint + "new-doc",
      data,
      {
        responseType: "json",
        headers: httpHeaders,
      }
    );
  }

  updateDoc(data): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.put(
      this.utilsService.apiEndpoint + "update-doc",
      data,
      {
        responseType: "json",
        headers: httpHeaders,
      }
    );
  }

  removeDoc(id): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.delete(
      this.utilsService.apiEndpoint + "delete-doc/" + id,
      {
        responseType: "json",
        headers: httpHeaders,
      }
    );
  }

  addFaq(data): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.post(
      this.utilsService.apiEndpoint + "new-faq",
      data,
      {
        responseType: "json",
        headers: httpHeaders,
      }
    );
  }

  updateFaq(data): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.put(
      this.utilsService.apiEndpoint + "update-faq",
      data,
      {
        responseType: "json",
        headers: httpHeaders,
      }
    );
  }

  removeFaq(id): Observable<any> {
    let httpHeaders = new HttpHeaders().set("Content-Type", "application/json");
    return this.httpClient.delete(
      this.utilsService.apiEndpoint + "delete-faq/" + id,
      {
        responseType: "json",
        headers: httpHeaders,
      }
    );
  }
}



// WEBPACK FOOTER //
// /Users/ugyen/Documents/Bhutan-office/my_bhutan/mybhutan-travel/src-admin/app/services/api.service.ts